// apps/dashboard/src/components/UpgradeModal.tsx
// Modal for 1-click plan upgrade/checkout.
// Displays the plan catalogue with quotas and initiates a Stripe/Asaas checkout session.
// SPEC: docs/specs/tenant/sprint-ak-self-serve-trial.spec.md §7.2

import { useCreateCheckoutSession } from '@/hooks/use-checkout';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@ominiflow/ui';
import { CheckCircle2, Loader2, Zap } from 'lucide-react';
import { useState } from 'react';

export interface PlanInfo {
  planCode: string;
  label: string;
  priceLabel: string;
  messages: number;
  agents: number;
  numbers: number;
  hasOverage: boolean;
  highlight?: boolean;
}

export interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan: string;
  plans?: PlanInfo[];
}

/** Default paid plans shown in the modal (excludes FREE and ENTERPRISE) */
const DEFAULT_PLANS: PlanInfo[] = [
  {
    planCode: 'STARTER',
    label: 'Starter',
    priceLabel: 'R$ 199/mês',
    messages: 10_000,
    agents: 5,
    numbers: 2,
    hasOverage: true,
  },
  {
    planCode: 'PRO',
    label: 'Pro',
    priceLabel: 'R$ 699/mês',
    messages: 100_000,
    agents: 20,
    numbers: 5,
    hasOverage: true,
    highlight: true,
  },
  {
    planCode: 'BUSINESS',
    label: 'Business',
    priceLabel: 'R$ 1.499/mês',
    messages: 500_000,
    agents: 100,
    numbers: 20,
    hasOverage: true,
  },
];

function formatMessages(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M/mês`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}.000/mês`;
  return `${n}/mês`;
}

export function UpgradeModal({ open, onClose, currentPlan, plans }: UpgradeModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const displayPlans = plans ?? DEFAULT_PLANS;
  const defaultSelected =
    displayPlans.find((p) => p.highlight)?.planCode ?? displayPlans[0]?.planCode ?? null;
  const activePlan = selected ?? defaultSelected;

  // useCreateCheckoutSession already redirects via onSuccess
  const { mutate: startCheckout, isPending } = useCreateCheckoutSession();

  const handleContinue = () => {
    if (!activePlan) return;
    startCheckout({ planCode: activePlan, provider: 'STRIPE' });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Escolha seu plano
          </DialogTitle>
        </DialogHeader>

        {/* Plan cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 py-2">
          {displayPlans.map((plan) => {
            const isActive = activePlan === plan.planCode;
            const isCurrent = currentPlan === plan.planCode;
            return (
              <button
                type="button"
                key={plan.planCode}
                onClick={() => setSelected(plan.planCode)}
                className={`relative flex flex-col gap-3 rounded-lg border-2 p-4 text-left transition-all ${
                  isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-medium text-primary-foreground whitespace-nowrap">
                    ⭐ Recomendado
                  </span>
                )}
                <div>
                  <p className="font-semibold text-sm">{plan.label}</p>
                  <p className="text-lg font-bold">{plan.priceLabel}</p>
                </div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                    {formatMessages(plan.messages)} mensagens
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                    {plan.agents} agentes
                  </li>
                  <li className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                    {plan.numbers} número(s)
                  </li>
                  {plan.hasOverage && (
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                      Excedente habilitado
                    </li>
                  )}
                </ul>
                {isCurrent && (
                  <span className="text-[10px] font-medium text-primary">Plano atual</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleContinue} disabled={!activePlan || isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecionando…
              </>
            ) : (
              `Continuar com ${displayPlans.find((p) => p.planCode === activePlan)?.label ?? activePlan} →`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
